import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AiLeftmenu } from '../ai-leftmenu/ai-leftmenu';
import { AiHeader } from '../ai-header/ai-header';
import { HeaderService } from '../../shared/services/headerService';
import { AgentChatStateService } from '../../features/agentic-ai/agent-chat-state.service';
import { NetworkConnectivityService } from '../../shared/services/network-connectivity.service';
import { Resizewindow } from '../../shared/directives/resizewindow';

@Component({
  selector: 'app-ai-agent-layout',
  imports: [AiLeftmenu, AiHeader, RouterOutlet, Resizewindow],
  templateUrl: './ai-agent-layout.html',
  styleUrl: './ai-agent-layout.css',
})
export class AiAgentLayout implements OnInit, AfterViewInit {
  readonly network = inject(NetworkConnectivityService);
  private readonly headerService = inject(HeaderService);
  private readonly agentChatState = inject(AgentChatStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  /** When true, main pane shows the welcome background image. */
  readonly headerVisible = signal(true);
  /** Right split panel (email, notes, and similar) is open — keep the title bar put. */
  private readonly aiSidebarOpen = toSignal(this.headerService.getAiSidebarVisibility(), {
    initialValue: false,
  });
  /**
   * Conversation title bar collapsed while the user scrolls the center chat down;
   * expands again on scroll up (or when near the top).
   */
  readonly chromeHeaderCollapsed = signal(false);
  sidebarCollapsed = false;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private readonly routePath = computed(() =>
    this.currentUrl().split(/[?#]/)[0].replace(/\/$/, ''),
  );

  readonly onWorkflowBuilder = computed(
    () =>
      this.routePath() === '/workflow-builder' ||
      this.routePath().endsWith('/workflow-builder'),
  );

  /**
   * Welcome screen (`/ai-agent` before the first prompt) has nothing to show in the title bar,
   * so it stays hidden until submitting a prompt sets the conversation title.
   */
  private readonly onEmptyNewChat = computed(() => {
    return this.routePath() === '/ai-agent' && !this.agentChatState.conversationHeaderTitle();
  });

  /** Offline notice keeps the bar on screen even with no title. Hidden entirely on workflow-builder. */
  readonly headerBarHidden = computed(
    () =>
      this.onWorkflowBuilder() ||
      this.chromeHeaderCollapsed() ||
      (this.onEmptyNewChat() && !this.network.showOfflineHeader()),
  );

  @ViewChild('rightBlock', { static: true })
  private readonly rightBlock?: ElementRef<HTMLElement>;

  private lastScrollTopByTarget = new WeakMap<HTMLElement, number>();
  private readonly scrollDeltaThresholdPx = 8;
  private readonly topRevealPx = 12;

  constructor() {
    let wasOffline = !this.network.isOnline();
    effect(() => {
      const online = this.network.isOnline();
      if (wasOffline && online) {
        this.agentChatState.refreshConversations();
      }
      wasOffline = !online;
    });

    // New / switched conversation — always show the title bar again.
    effect(() => {
      this.agentChatState.activeConversationId();
      this.agentChatState.conversationHeaderTitle();
      this.chromeHeaderCollapsed.set(false);
      this.lastScrollTopByTarget = new WeakMap<HTMLElement, number>();
    });

    effect(() => {
      if (this.aiSidebarOpen()) {
        this.chromeHeaderCollapsed.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.headerService
      .getHeader()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((visible) => {
        this.headerVisible.set(visible);
      });
    /** Avoid protected conversation list calls on sessionless local workflow-builder. */
    if (!this.onWorkflowBuilder()) {
      this.agentChatState.refreshConversations();
    }
  }

  ngAfterViewInit(): void {
    const root = this.rightBlock?.nativeElement;
    if (!root) {
      return;
    }

    const onScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      if (!root.contains(target) && target !== root) {
        return;
      }
      // Split compose/detail panels keep the conversation title and chat box in place.
      if (this.aiSidebarOpen()) {
        this.chromeHeaderCollapsed.set(false);
        return;
      }
      // Ignore tiny nested scroll areas (chips, code blocks, etc.).
      if (target !== root && target.clientHeight < 180) {
        return;
      }

      const scrollTop = target.scrollTop;
      if (scrollTop <= this.topRevealPx) {
        this.chromeHeaderCollapsed.set(false);
        this.lastScrollTopByTarget.set(target, scrollTop);
        return;
      }

      const prev = this.lastScrollTopByTarget.get(target) ?? scrollTop;
      const delta = scrollTop - prev;
      this.lastScrollTopByTarget.set(target, scrollTop);
      if (Math.abs(delta) < this.scrollDeltaThresholdPx) {
        return;
      }

      this.chromeHeaderCollapsed.set(delta > 0);
    };

    // `scroll` does not bubble; capture so nested chat scrollers still drive the chrome.
    root.addEventListener('scroll', onScroll, true);
    this.destroyRef.onDestroy(() => {
      root.removeEventListener('scroll', onScroll, true);
    });
  }
}
